/**
 * Drawer showing a single workflow execution's full trace.
 *
 * Opened from the Workflow Runs page when a row is clicked. Lighter
 * than TestRunDrawer because it has no controls — just the per-node
 * timeline and the error (if any).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, ArrowUpRight, Check, CheckCircle2, Clock, GitBranch, GitFork, Loader2, PauseCircle, RotateCcw, Send, Square, X } from "lucide-react";
import { Link } from "react-router-dom";

import { deliveriesApi } from "@/lib/api-deliveries";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  workflowExecutionsApi,
  type ChildExecutionRef,
  type NodeExecutionSummary,
} from "@/lib/api-workflows";
import { usePermissions } from "@/lib/permissions";

const STATUS_BADGE: Record<string, { tone: "active" | "danger" | "neutral" | "warning"; label: string }> = {
  succeeded: { tone: "active", label: "ok" },
  failed: { tone: "danger", label: "failed" },
  running: { tone: "warning", label: "running" },
  cancelled: { tone: "neutral", label: "cancelled" },
  skipped: { tone: "neutral", label: "skipped" },
  pending: { tone: "neutral", label: "pending" },
  waiting: { tone: "warning", label: "in attesa" },
};

function NodeRow({ n }: { n: NodeExecutionSummary }) {
  const badge = STATUS_BADGE[n.status] ?? STATUS_BADGE.skipped;
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-xs">
      <Badge tone={badge.tone}>{badge.label}</Badge>
      <div className="min-w-0">
        <div className="truncate font-mono">{n.node_id}</div>
        <div className="truncate text-xs text-muted-foreground">
          {n.node_kind}
        </div>
      </div>
      <div className="font-mono text-xs text-muted-foreground">
        {n.duration_ms ?? "—"}ms
      </div>
      {n.error && (
        <div className="col-span-4 mt-1 border-t border-border pt-1 font-mono text-xs text-destructive">
          {n.error.split("\n").slice(0, 4).join("\n")}
        </div>
      )}
    </div>
  );
}

export interface ExecutionDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  executionId: string | null;
  /**
   * Optional callback fired when the user clicks a parent / child link
   * inside the drawer. Lets the parent page swap which execution is
   * displayed without closing+reopening.
   */
  onNavigateExecution?: (id: string) => void;
}

function ChildRow({
  c,
  onClick,
}: {
  c: ChildExecutionRef;
  onClick?: () => void;
}) {
  const badge = STATUS_BADGE[c.status] ?? STATUS_BADGE.skipped;
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-left text-xs hover:bg-accent/5"
    >
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
      <div className="min-w-0">
        <div className="truncate font-medium">{c.workflow_name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {c.direction} · {c.trigger}
        </div>
      </div>
      <Badge tone={badge.tone}>{badge.label}</Badge>
    </button>
  );
}

export function ExecutionDetailDrawer({
  open,
  onOpenChange,
  executionId,
  onNavigateExecution,
}: ExecutionDetailDrawerProps) {
  const { can } = usePermissions();
  const canExecute = can("executions", "execute");
  const canApprove = can("executions", "approve");
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["execution", executionId],
    queryFn: () =>
      executionId ? workflowExecutionsApi.get(executionId) : Promise.reject("no id"),
    enabled: open && !!executionId,
  });

  const retryMutation = useMutation({
    mutationFn: () => workflowExecutionsApi.retry(executionId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-runs"] });
    },
  });
  const cancelMutation = useMutation({
    mutationFn: () => workflowExecutionsApi.cancel(executionId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-runs"] });
      qc.invalidateQueries({ queryKey: ["execution", executionId] });
    },
  });
  // ADR-0066 S2 — approve/reject a run parked on an approval wait.
  const resumeMutation = useMutation({
    mutationFn: (decision: "approved" | "rejected") =>
      workflowExecutionsApi.resume(executionId!, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-runs"] });
      qc.invalidateQueries({ queryKey: ["execution", executionId] });
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" width="w-[56rem]">
        <SheetHeader>
          {/* SheetContent already renders a close X in the top-right
              corner — no need to duplicate it here. */}
          <div className="flex items-center gap-2">
            <SheetTitle>Execution detail</SheetTitle>
            {executionId && (
              <Button
                asChild
                size="sm"
                variant="ghost"
                className="ml-auto h-7 gap-1.5 text-xs"
              >
                <Link to={`/trace/execution/${executionId}`}>
                  <GitBranch className="h-3.5 w-3.5" strokeWidth={1.5} />
                  View trace
                </Link>
              </Button>
            )}
          </div>
        </SheetHeader>
        <SheetBody className="space-y-3 overflow-y-auto">
          {query.isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}
          {query.isError && (
            <Alert tone="danger">
              {(query.error as Error)?.message ?? "Failed to load execution"}
            </Alert>
          )}
          {query.data && (
            <>
              {/* Header card */}
              <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  {query.data.status === "succeeded" ? (
                    <CheckCircle2 className="h-4 w-4 text-success" strokeWidth={1.5} />
                  ) : query.data.status === "failed" ? (
                    <AlertTriangle className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                  ) : query.data.status === "waiting" ? (
                    <PauseCircle className="h-4 w-4 text-warning" strokeWidth={1.5} />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  )}
                  <span className="font-medium">{query.data.status}</span>
                  {query.data.status === "waiting" && (
                    <Badge tone="warning">
                      {query.data.awaiting_approval ? "In attesa di approvazione" : "In pausa"}
                    </Badge>
                  )}
                  <Badge tone="neutral">{query.data.trigger}</Badge>
                  {query.data.workflow_version && (
                    <Badge tone="neutral">v{query.data.workflow_version}</Badge>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  <Link
                    to={`/workflows/${query.data.workflow_id}`}
                    className="hover:text-accent"
                  >
                    {query.data.workflow_name}
                  </Link>
                  {" · "}
                  {new Date(query.data.started_at).toLocaleString()}
                </div>
                {query.data.error && (
                  <pre className="mt-2 whitespace-pre-wrap border-t border-border pt-2 font-mono text-xs text-destructive">
                    {query.data.error}
                  </pre>
                )}

                {/* Admin actions: retry on terminal-failed runs,
                    cancel on running runs. */}
                {canExecute && (
                  <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
                    {(query.data.status === "failed" || query.data.status === "cancelled") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => retryMutation.mutate()}
                        disabled={retryMutation.isPending}
                      >
                        {retryMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
                        )}
                        Retry
                      </Button>
                    )}
                    {(query.data.status === "running" || query.data.status === "waiting") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelMutation.mutate()}
                        disabled={cancelMutation.isPending}
                      >
                        {cancelMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Square className="h-4 w-4" strokeWidth={1.5} />
                        )}
                        Cancel
                      </Button>
                    )}
                    {retryMutation.isSuccess && (
                      <span className="text-xs text-success">
                        Retry queued — check the runs list
                      </span>
                    )}
                    {cancelMutation.isSuccess && (
                      <span className="text-xs text-muted-foreground">
                        Cancellation requested
                      </span>
                    )}
                  </div>
                )}

                {/* ADR-0066 S2 — approve/reject a run parked on an approval
                    wait (separate `executions:approve` permission). */}
                {canApprove && query.data.status === "waiting" && query.data.awaiting_approval && (
                  <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resumeMutation.mutate("approved")}
                      disabled={resumeMutation.isPending}
                    >
                      {resumeMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 text-success" strokeWidth={1.5} />
                      )}
                      Approva
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resumeMutation.mutate("rejected")}
                      disabled={resumeMutation.isPending}
                    >
                      <X className="h-4 w-4 text-danger" strokeWidth={1.5} />
                      Rifiuta
                    </Button>
                    {resumeMutation.isSuccess && (
                      <span className="text-xs text-muted-foreground">Decisione inviata</span>
                    )}
                  </div>
                )}
              </div>

              {/* Parent link */}
              {query.data.parent_execution_id && (
                <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Triggered by
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      onNavigateExecution?.(query.data!.parent_execution_id!)
                    }
                    className="mt-1 inline-flex items-center gap-1 text-foreground hover:text-accent"
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    parent execution
                    <span className="font-mono text-xs text-muted-foreground">
                      {query.data.parent_execution_id.slice(0, 8)}
                    </span>
                  </button>
                </div>
              )}

              {/* Per-node timeline */}
              <div className="space-y-1.5">
                {query.data.nodes.map((n) => (
                  <NodeRow key={n.id} n={n} />
                ))}
                {query.data.nodes.length === 0 && (
                  <div className="border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                    No node-level trace recorded.
                  </div>
                )}
              </div>

              {/* Outbound deliveries spawned by this execution.
                  Rendered between the node trace and the canonical-
                  event children because deliveries are the direct
                  result of this run, whereas children are downstream
                  workflows triggered by it. */}
              <DeliveriesSection executionId={query.data.id} />

              {/* Children (canonical event chain) */}
              {query.data.children.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <GitFork className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Triggered {query.data.children.length} child run
                    {query.data.children.length > 1 ? "s" : ""}
                  </div>
                  {query.data.children.map((c) => (
                    <ChildRow
                      key={c.id}
                      c={c}
                      onClick={() => onNavigateExecution?.(c.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}


// ---------------------------------------------------------------------------
// Deliveries section — outbound rows linked to this execution.
// Rendered between node trace and child executions because deliveries
// are the direct outbound consequence of this run.
// ---------------------------------------------------------------------------

const DELIVERY_TONE: Record<
  string,
  { tone: "active" | "danger" | "neutral" | "warning"; label: string }
> = {
  succeeded:   { tone: "active",  label: "delivered" },
  pending:     { tone: "neutral", label: "pending" },
  in_flight:   { tone: "neutral", label: "in flight" },
  failed:      { tone: "danger",  label: "failed" },
  dead_letter: { tone: "danger",  label: "dead letter" },
};


function DeliveriesSection({ executionId }: { executionId: string }) {
  const q = useQuery({
    queryKey: ["deliveries", "by-execution", executionId],
    queryFn: () =>
      deliveriesApi.list({
        source_execution_id: executionId,
        limit: 50,
      }),
    // Match the execution-detail polling interval used elsewhere in
    // the drawer so the two stay visually in step.
    refetchInterval: 3_000,
  });

  const items = q.data?.items ?? [];
  if (q.isLoading) {
    return null; // silent during initial fetch — drawer is already crowded
  }
  if (items.length === 0) {
    return null; // execution had no sinks; UI stays clean
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
        Spawned {items.length} {items.length === 1 ? "delivery" : "deliveries"}
      </div>
      {items.map((d) => {
        const badge = DELIVERY_TONE[d.status] ?? DELIVERY_TONE.pending;
        return (
          <Link
            key={d.id}
            to={`/deliveries`}
            // We could deep-link to /deliveries?id=… once that
            // route accepts query params; for now navigation lands on
            // the list and the user can find the row.
            className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-xs hover:bg-accent/5"
            title={d.last_error ?? undefined}
          >
            <div className="min-w-0 truncate">
              <span className="font-mono">{d.target_key}</span>
              <span className="ml-2 text-muted-foreground">{d.sink_kind}</span>
            </div>
            <span className="font-mono text-[10px] text-muted-foreground">
              {d.attempts}/{d.max_attempts}
            </span>
            <Badge tone={badge.tone}>{badge.label}</Badge>
          </Link>
        );
      })}
    </div>
  );
}
